import type { ApiConfiguredNotification } from '#/api/notification/types/ApiConfiguredNotification';
import type { ApiExplorerResourceNotification } from '#/api/notification/types/ApiExplorerResourceNotification';
import type { ApiMentionListingApplicationNotification } from '#/api/notification/types/ApiMentionListingApplicationNotification';
import type { ApiMentionSuggestedApplicantNotification } from '#/api/notification/types/ApiMentionSuggestedApplicantNotification';
import type { ApiNewFollowerNotification } from '#/api/notification/types/ApiNewFollowerNotification';
import type { ApiNewListingApplicationNotification } from '#/api/notification/types/ApiNewListingApplicationNotification';
import type { ApiNewRsvpNotification } from '#/api/notification/types/ApiNewRsvpNotification';
import type { ApiNewSuggestedApplicantsNotification } from '#/api/notification/types/ApiNewSuggestedApplicantsNotification';
import type { ApiNotification } from '#/api/notification/types/ApiNotification';
import type { ApiOpenwebEventCommentNotification } from '#/api/notification/types/ApiOpenwebEventCommentNotification';
import type { ApiOpenwebLikedCommentNotification } from '#/api/notification/types/ApiOpenwebLikedCommentNotification';
import type { ApiOpenwebRepliedCommentNotification } from '#/api/notification/types/ApiOpenwebRepliedCommentNotification';
import type { ApiOpenwebUserMentionedNotification } from '#/api/notification/types/ApiOpenwebUserMentionedNotification';
import type { ApplicantsByListingMappedNotification } from '#/types/mappedNotifications/ApplicantsByListingMappedNotification';
import type { MappedNotification } from '#/types/mappedNotifications/MappedNotification';
import type { NewSuggestedApplicantsByJobMappedNotification } from '#/types/mappedNotifications/NewSuggestedApplicantsByJobMappedNotification';
import type { RsvpsByListingMappedNotification } from '#/types/mappedNotifications/RsvpsByListingMappedNotification';

import { sortBy } from './functional/array';

export const getMappedNotificationIds = (
  notification: MappedNotification | ApiNotification,
) => {
  if (notification.type === 'NEW_APPLICATIONS_BY_LISTING') {
    return notification.data.notifications.map((n) => n.id);
  }
  if (notification.type === 'NEW_SUGGESTED_APPLICANTS_BY_JOB') {
    return notification.data.notifications.map((n) => n.id);
  }
  if (notification.type === 'NEW_RSVP_BY_LISTING') {
    return notification.data.notifications.map((n) => n.id);
  }
  if (notification.type === 'MENTION_LISTING_APPLICATION') {
    return [notification.id];
  }
  if (notification.type === 'MENTION_SUGGESTED_APPLICANT') {
    return [notification.id];
  }
  if (notification.type === 'OPENWEB_REPLIED_COMMENT') {
    return [notification.id];
  }
  if (notification.type === 'OPENWEB_LIKED_COMMENT') {
    return [notification.id];
  }
  if (notification.type === 'OPENWEB_USER_MENTIONED') {
    return [notification.id];
  }
  if (notification.type === 'CONFIGURED_NOTIFICATION') {
    return [notification.id];
  }
  if (notification.type === 'NEW_FOLLOWER') {
    return [notification.id];
  }
  if (notification.type === 'EXPLORER_RESOURCE') {
    return [notification.id];
  }
  if (notification.type === 'OPENWEB_EVENT_COMMENT') {
    return [notification.id];
  }

  throw Error('Unknown notification type');
};

/*
 * Sort and group notifications.
 *
 * Notifications of type "NEW_LISTING_APPLICATION" and "NEW_SUGGESTED_APPLICANTS"
 * are grouped by listing id.
 *
 * All notifications are sorted by creation date.
 *
 */

export function sortNotifications(unsortedNotifications: ApiNotification[]) {
  const {
    applicantNotifications,
    suggestedApplicantsNotifications,
    applicantMentionNotifications,
    suggestedApplicantMentionNotifications,
    openwebRepliedCommentNotifications,
    openwebLikedCommentNotifications,
    openwebUserMentionedNotifications,
    configuredNotifications,
    newFollowerNotifications,
    explorerResourceNotifications,
    rsvpNotifications,
    openwebEventCommentNotifications,
  } = unsortedNotifications.reduce(
    (acc, notification) => {
      if (notification.type === 'NEW_LISTING_APPLICATION') {
        acc.applicantNotifications.push(notification);
      } else if (notification.type === 'NEW_SUGGESTED_APPLICANTS') {
        acc.suggestedApplicantsNotifications.push(notification);
      } else if (notification.type === 'MENTION_LISTING_APPLICATION') {
        acc.applicantMentionNotifications.push(notification);
      } else if (notification.type === 'MENTION_SUGGESTED_APPLICANT') {
        acc.suggestedApplicantMentionNotifications.push(notification);
      } else if (notification.type === 'OPENWEB_REPLIED_COMMENT') {
        acc.openwebRepliedCommentNotifications.push(notification);
      } else if (notification.type === 'OPENWEB_LIKED_COMMENT') {
        acc.openwebLikedCommentNotifications.push(notification);
      } else if (notification.type === 'OPENWEB_USER_MENTIONED') {
        acc.openwebUserMentionedNotifications.push(notification);
      } else if (notification.type === 'CONFIGURED_NOTIFICATION') {
        acc.configuredNotifications.push(notification);
      } else if (notification.type === 'NEW_FOLLOWER') {
        acc.newFollowerNotifications.push(notification);
      } else if (notification.type === 'EXPLORER_RESOURCE') {
        acc.explorerResourceNotifications.push(notification);
      } else if (notification.type === 'NEW_RSVP') {
        acc.rsvpNotifications.push(notification);
      } else if (notification.type === 'OPENWEB_EVENT_COMMENT') {
        acc.openwebEventCommentNotifications.push(notification);
      }

      return acc;
    },
    {
      applicantNotifications: [] as ApiNewListingApplicationNotification[],
      suggestedApplicantsNotifications:
        [] as ApiNewSuggestedApplicantsNotification[],
      applicantMentionNotifications:
        [] as ApiMentionListingApplicationNotification[],
      suggestedApplicantMentionNotifications:
        [] as ApiMentionSuggestedApplicantNotification[],
      openwebRepliedCommentNotifications:
        [] as ApiOpenwebRepliedCommentNotification[],
      openwebLikedCommentNotifications:
        [] as ApiOpenwebLikedCommentNotification[],
      openwebUserMentionedNotifications:
        [] as ApiOpenwebUserMentionedNotification[],
      configuredNotifications: [] as ApiConfiguredNotification[],
      newFollowerNotifications: [] as ApiNewFollowerNotification[],
      explorerResourceNotifications: [] as ApiExplorerResourceNotification[],
      rsvpNotifications: [] as ApiNewRsvpNotification[],
      openwebEventCommentNotifications:
        [] as ApiOpenwebEventCommentNotification[],
    },
  );

  const applicantNotificationsByListingId: Record<
    string,
    ApplicantsByListingMappedNotification
  > = applicantNotifications.reduce(
    (
      acc: Record<string, ApplicantsByListingMappedNotification>,
      notification,
    ) => {
      const { listing } = notification.data.listingApplication;
      const mappedNotification = acc[listing.id] || {
        type: 'NEW_APPLICATIONS_BY_LISTING',
        created: notification.created,
        read: notification.read,
        viewed: notification.viewed,
        about: notification.about,
        data: {
          notifications: [],
          listing,
          count: 0,
        },
      };
      mappedNotification.data.notifications.push(notification);
      if (
        new Date(mappedNotification.created) < new Date(notification.created)
      ) {
        mappedNotification.created = notification.created;
      }
      if (mappedNotification.viewed !== null && notification.viewed === null) {
        mappedNotification.viewed = null;
      }
      if (mappedNotification.read !== null && notification.read === null) {
        mappedNotification.read = null;
        mappedNotification.data.count = 0;
      }
      mappedNotification.data.count += 1;
      return Object.assign(acc, { [listing.id]: mappedNotification });
    },
    {},
  );

  const suggestedApplicantNotificationsByJobId: Record<
    string,
    NewSuggestedApplicantsByJobMappedNotification
  > = suggestedApplicantsNotifications.reduce(
    (
      acc: Record<string, NewSuggestedApplicantsByJobMappedNotification>,
      notification,
    ) => {
      const { job } = notification.data.suggestedApplicantBatch;

      const mappedNotification = acc[job.id] || {
        type: 'NEW_SUGGESTED_APPLICANTS_BY_JOB',
        created: notification.created,
        read: notification.read,
        viewed: notification.viewed,
        about: notification.about,
        data: {
          notifications: [],
          job,
          count: 0,
        },
      };
      mappedNotification.data.notifications.push(notification);
      if (
        new Date(mappedNotification.created) < new Date(notification.created)
      ) {
        mappedNotification.created = notification.created;
      }
      if (mappedNotification.viewed !== null && notification.viewed === null) {
        mappedNotification.viewed = null;
      }
      if (mappedNotification.read !== null && notification.read === null) {
        mappedNotification.read = null;
        mappedNotification.data.count = 0;
      }
      mappedNotification.data.count +=
        notification.data.suggestedApplicantBatch.applicantCount;
      return Object.assign(acc, { [job.id]: mappedNotification });
    },
    {},
  );

  const rsvpNotificationsByListingId: Record<
    string,
    RsvpsByListingMappedNotification
  > = rsvpNotifications.reduce(
    (acc: Record<string, RsvpsByListingMappedNotification>, notification) => {
      const { event } = notification.data.rsvp;
      const mappedNotification = acc[event.id] || {
        type: 'NEW_RSVP_BY_LISTING',
        created: notification.created,
        read: notification.read,
        viewed: notification.viewed,
        about: notification.about,
        data: {
          notifications: [],
          event,
          count: 0,
        },
      };
      mappedNotification.data.notifications.push(notification);
      if (
        new Date(mappedNotification.created) < new Date(notification.created)
      ) {
        mappedNotification.created = notification.created;
      }
      if (mappedNotification.viewed !== null && notification.viewed === null) {
        mappedNotification.viewed = null;
      }
      if (mappedNotification.read !== null && notification.read === null) {
        mappedNotification.read = null;
        mappedNotification.data.count = 0;
      }
      mappedNotification.data.count += 1;
      return Object.assign(acc, { [event.id]: mappedNotification });
    },
    {},
  );

  const notifications: (ApiNotification | MappedNotification)[] = [
    // Grouped
    ...Object.values(applicantNotificationsByListingId),
    ...Object.values(suggestedApplicantNotificationsByJobId),
    ...Object.values(rsvpNotificationsByListingId),
    // Un-grouped
    ...Object.values(applicantMentionNotifications),
    ...Object.values(suggestedApplicantMentionNotifications),
    ...Object.values(openwebRepliedCommentNotifications),
    ...Object.values(openwebLikedCommentNotifications),
    ...Object.values(openwebUserMentionedNotifications),
    ...Object.values(configuredNotifications),
    ...Object.values(newFollowerNotifications),
    ...Object.values(explorerResourceNotifications),
    ...Object.values(openwebEventCommentNotifications),
  ];

  return sortBy(notifications, (n) => new Date(n.created), true);
}
